import React from 'react';
import { ChevronRight, RefreshCw, Plus, Trash, Edit, Facebook, Twitter } from 'lucide-react';

// 메뉴 카드 컴포넌트
export const MenuCard = ({ menu, animating, handleShare }) => {
  if (!menu) return null;
  
  return (
    <div className={`menu-card ${animating ? 'pulse' : ''}`}>
      <div className="menu-card-header">
        <h2 className="menu-name">{menu.name}</h2>
        <span className="menu-emoji">{menu.emoji}</span>
        <p className="menu-category">{menu.category}</p>
      </div>
      
      <div className="menu-card-body">
        <p className="menu-description">{menu.description}</p>
        
        <div className="menu-details">
          <div className="menu-detail-item">
            <p className="detail-label">칼로리</p>
            <p className="detail-value">{menu.calories}</p>
          </div>
          <div className="menu-detail-item">
            <p className="detail-label">가격대</p>
            <p className="detail-value">{menu.price}</p>
          </div>
        </div>
        
        <div className="menu-message">
          <p>이 메뉴는 어떠세요? 맛있게 드세요! 😋</p>
        </div>
        
        {/* 소셜 공유 버튼 */}
        <SocialShare handleShare={handleShare} />
      </div>
    </div>
  );
};

// 소셜 공유 컴포넌트
export const SocialShare = ({ handleShare }) => {
  return (
    <div className="social-share">
      <p className="share-label">공유하기:</p>
      <div className="share-buttons">
        <button onClick={() => handleShare('facebook')} className="share-btn facebook">
          <Facebook size={16} />
        </button>
        <button onClick={() => handleShare('twitter')} className="share-btn twitter">
          <Twitter size={16} />
        </button>
        <button onClick={() => handleShare('kakao')} className="share-btn kakao">
          K
        </button>
        <button onClick={() => handleShare('clipboard')} className="share-btn clipboard">
          📋
        </button>
      </div>
    </div>
  );
};

// 카테고리 필터 컴포넌트
export const CategoryFilters = ({ categories, filter, setFilter }) => {
  return (
    <div className="category-filters">
      <button 
        onClick={() => setFilter('전체')} 
        className={`category-btn ${filter === '전체' ? 'active' : ''}`}
      >
        전체
      </button>
      {categories.map(category => (
        <button 
          key={category}
          onClick={() => setFilter(category)} 
          className={`category-btn ${filter === category ? 'active' : ''}`}
        >
          {category}
        </button>
      ))}
    </div>
  );
};

// 추천 버튼 컴포넌트
export const RecommendButton = ({ getRandomMenu, animating, disabled }) => {
  return (
    <button 
      onClick={getRandomMenu} 
      disabled={animating || disabled}
      className="recommend-btn"
    >
      {animating ? (
        <>
          <RefreshCw className="icon spin" />
          <span>메뉴 고르는 중...</span>
        </>
      ) : (
        <>
          <ChevronRight className="icon" />
          <span>오늘의 점심 추천받기</span>
        </>
      )}
    </button>
  );
};

// 관리자 버튼 컴포넌트
export const AdminButtons = ({ 
  showAddForm, setShowAddForm, 
  showEditMode, setShowEditMode, 
  editingMenu, setEditingMenu, 
  handleResetMenus 
}) => {
  return (
    <div className="admin-buttons">
      <button 
        onClick={() => {
          setShowAddForm(!showAddForm);
          if (showEditMode) setShowEditMode(false);
          if (editingMenu) setEditingMenu(null);
        }}
        className="admin-btn add"
      >
        <Plus size={14} className="icon" /> 
        {showAddForm ? '추가 취소' : '새 메뉴 추가'}
      </button>
      
      <button 
        onClick={() => {
          setShowEditMode(!showEditMode);
          if (showAddForm) setShowAddForm(false);
          if (editingMenu) setEditingMenu(null);
        }}
        className="admin-btn edit"
      >
        <Edit size={14} className="icon" /> 
        {showEditMode ? '관리 모드 종료' : '메뉴 관리'}
      </button>
      
      <button 
        onClick={handleResetMenus}
        className="admin-btn reset"
      >
        <RefreshCw size={14} className="icon" /> 
        초기 메뉴 리셋
      </button>
    </div>
  );
};

// 메뉴 추가 폼 컴포넌트
export const AddMenuForm = ({
  newMenu,
  handleNewMenuChange,
  categories,
  newCategory,
  setNewCategory,
  handleAddCategory,
  emojiOptions,
  handleEmojiSelect,
  setShowAddForm,
  handleAddMenu
}) => {
  return (
    <div className="add-menu-form">
      <h2 className="form-title">새 메뉴 추가</h2>
      
      <div className="form-fields">
        <div className="form-field">
          <label className="field-label">
            메뉴 이름 *
          </label>
          <input
            type="text"
            name="name"
            value={newMenu.name}
            onChange={handleNewMenuChange}
            className="field-input"
            placeholder="메뉴 이름을 입력하세요"
            required
          />
        </div>
        
        <div className="form-field">
          <label className="field-label">
            설명
          </label>
          <textarea
            name="description"
            value={newMenu.description}
            onChange={handleNewMenuChange}
            className="field-textarea"
            placeholder="메뉴에 대한 간단한 설명을 입력하세요"
            rows="2"
          />
        </div>
        
        <div className="form-row">
          <div className="form-field half">
            <label className="field-label">
              칼로리
            </label>
            <input
              type="text"
              name="calories"
              value={newMenu.calories}
              onChange={handleNewMenuChange}
              className="field-input"
              placeholder="예: 약 500kcal"
            />
          </div>
          
          <div className="form-field half">
            <label className="field-label">
              가격대
            </label>
            <input
              type="text"
              name="price"
              value={newMenu.price}
              onChange={handleNewMenuChange}
              className="field-input"
              placeholder="예: 8,000원~10,000원"
            />
          </div>
        </div>
        
        <div className="form-field">
          <label className="field-label">
            카테고리 *
          </label>
          <div className="field-with-button">
            <select
              name="category"
              value={newMenu.category}
              onChange={handleNewMenuChange}
              className="field-select"
              required
            >
              <option value="">카테고리 선택</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-field">
          <label className="field-label">
            새 카테고리 추가
          </label>
          <div className="field-with-button">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="field-input"
              placeholder="새 카테고리 이름"
            />
            <button
              onClick={handleAddCategory}
              className="field-button"
              type="button"
            >
              추가
            </button>
          </div>
        </div>
        
        <div className="form-field">
          <label className="field-label">
            이모지 선택
          </label>
          <div className="emoji-picker">
            {emojiOptions.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => handleEmojiSelect(emoji)}
                className={`emoji-btn ${newMenu.emoji === emoji ? 'selected' : ''}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
        
        <div className="form-buttons">
          <button
            type="button"
            onClick={() => setShowAddForm(false)}
            className="cancel-btn"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleAddMenu}
            className="submit-btn"
          >
            메뉴 추가
          </button>
        </div>
      </div>
    </div>
  );
};

// 메뉴 수정 폼 컴포넌트
export const EditMenuForm = ({
  editingMenu,
  handleEditMenuChange,
  categories,
  newCategory,
  setNewCategory,
  handleAddCategory,
  emojiOptions,
  handleEditEmojiSelect,
  handleEditCancel,
  handleEditSave
}) => {
  return (
    <div className="edit-menu-form">
      <h2 className="form-title">메뉴 수정</h2>
      
      <div className="form-fields">
        <div className="form-field">
          <label className="field-label">
            메뉴 이름 *
          </label>
          <input
            type="text"
            name="name"
            value={editingMenu.name}
            onChange={handleEditMenuChange}
            className="field-input"
            placeholder="메뉴 이름을 입력하세요"
            required
          />
        </div>
        
        <div className="form-field">
          <label className="field-label">
            설명
          </label>
          <textarea
            name="description"
            value={editingMenu.description}
            onChange={handleEditMenuChange}
            className="field-textarea"
            placeholder="메뉴에 대한 간단한 설명을 입력하세요"
            rows="2"
          />
        </div>
        
        <div className="form-row">
          <div className="form-field half">
            <label className="field-label">
              칼로리
            </label>
            <input
              type="text"
              name="calories"
              value={editingMenu.calories}
              onChange={handleEditMenuChange}
              className="field-input"
              placeholder="예: 약 500kcal"
            />
          </div>
          
          <div className="form-field half">
            <label className="field-label">
              가격대
            </label>
            <input
              type="text"
              name="price"
              value={editingMenu.price}
              onChange={handleEditMenuChange}
              className="field-input"
              placeholder="예: 8,000원~10,000원"
            />
          </div>
        </div>
        
        <div className="form-field">
          <label className="field-label">
            카테고리 *
          </label>
          <div className="field-with-button">
            <select
              name="category"
              value={editingMenu.category}
              onChange={handleEditMenuChange}
              className="field-select"
              required
            >
              <option value="">카테고리 선택</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-field">
          <label className="field-label">
            새 카테고리 추가
          </label>
          <div className="field-with-button">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="field-input"
              placeholder="새 카테고리 이름"
            />
            <button
              onClick={handleAddCategory}
              className="field-button"
              type="button"
            >
              추가
            </button>
          </div>
        </div>
        
        <div className="form-field">
          <label className="field-label">
            이모지 선택
          </label>
          <div className="emoji-picker">
            {emojiOptions.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => handleEditEmojiSelect(emoji)}
                className={`emoji-btn ${editingMenu.emoji === emoji ? 'selected' : ''}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
        
        <div className="form-buttons">
          <button
            type="button"
            onClick={handleEditCancel}
            className="cancel-btn"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleEditSave}
            className="submit-btn"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

// 메뉴 목록 컴포넌트
export const MenuList = ({
  lunchMenus,
  filter,
  showEditMode,
  setSelectedMenu,
  handleEditStart,
  handleDeleteMenu
}) => {
  return (
    <div className="menu-list-container">
      <h3 className="list-title">
        {showEditMode ? '메뉴 편집 및 삭제' : '전체 메뉴 목록'}
      </h3>
      
      {lunchMenus.length === 0 ? (
        <p className="empty-message">등록된 메뉴가 없습니다. 새 메뉴를 추가해주세요.</p>
      ) : (
        <div className="menu-grid">
          {lunchMenus
            .filter(menu => filter === '전체' || menu.category === filter)
            .map((menu) => (
              <div 
                key={menu.id} 
                className={`menu-item ${showEditMode ? 'edit-mode' : ''}`}
                onClick={() => !showEditMode && setSelectedMenu(menu)}
              >
                <div className="menu-item-content">
                  <span className="item-emoji">{menu.emoji}</span>
                  <span className="item-name">{menu.name}</span>
                  <span className="item-category">{menu.category}</span>
                </div>
                
                {showEditMode && (
                  <div className="edit-buttons">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditStart(menu.id);
                      }}
                      className="edit-btn"
                      aria-label="수정"
                    >
                      <Edit size={16} className="icon" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMenu(menu.id);
                      }}
                      className="delete-btn"
                      aria-label="삭제"
                    >
                      <Trash size={16} className="icon" />
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// 알림 컴포넌트
export const Notification = ({ message }) => {
  if (!message) return null;
  
  return (
    <div className="notification">
      {message}
    </div>
  );
};

// 푸터 컴포넌트
export const Footer = () => {
  return (
    <footer className="footer">
      <p>© 2025 직장인 점심 메뉴 추천 서비스</p>
      <div className="social-links">
        <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link">
          <Facebook size={20} />
        </a>
        <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-link">
          <Twitter size={20} />
        </a>
        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link">
          Instagram
        </a>
      </div>
    </footer>
  );
};